package dev.danko.roblox_craft_ai.components

import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import dev.danko.roblox_craft_ai.uikit.components.MeshQualityChoices
import dev.danko.roblox_craft_ai.uikit.components.QualityCredits
import dev.danko.roblox_craft_ai.uikit.components.QualityPrefs
import dev.danko.roblox_craft_ai.uikit.config.BackendEndpoint
import dev.danko.roblox_craft_ai.state.Txt
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.danko.roblox_craft_ai.shared.model.ConceptGenerationResult
import dev.danko.roblox_craft_ai.uikit.components.AppButton
import dev.danko.roblox_craft_ai.uikit.components.AppButtonSize
import dev.danko.roblox_craft_ai.uikit.components.AppButtonVariant
import dev.danko.roblox_craft_ai.uikit.components.AppCard
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

val ConceptPreviewBoxStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .height(300.px)
        .backgroundColor(Colors.White)
        .borderRadius(0.5.cssRem)
        .border(1.px, LineStyle.Solid, colorMode.toAppPalette().border)
        .overflow(Overflow.Hidden)
}

val TokenSavingBadgeStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .padding(topBottom = 0.25.cssRem, leftRight = 0.6.cssRem)
        .borderRadius(9999.px)
        .backgroundColor(palette.accent.toRgb().copy(alpha = 35))
        .border(1.px, LineStyle.Solid, palette.accent)
        .color(palette.accent)
        .fontSize(0.75.cssRem)
        .fontWeight(FontWeight.SemiBold)
}

@Composable
fun ConceptApprovalCard(
    concept: ConceptGenerationResult,
    isApproving: Boolean,
    isRerolling: Boolean,
    onApprove: (ConceptGenerationResult) -> Unit,
    onReroll: (ConceptGenerationResult) -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    /** Credit price label of a usage kind ("MESH_3D_HIGH"…), null when unknown. */
    costOf: (String) -> String? = { null }
) {
    val theme = LocalAppTheme.current
    var showPromptDetails by remember { mutableStateOf(false) }

    AppCard(
        modifier = modifier
            .fillMaxWidth()
            .border(1.5.px, LineStyle.Solid, theme.colors.accent)
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(1.cssRem).gap(0.875.cssRem)
        ) {
            // Header
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(0.5.cssRem)) {
                    SpanText("🎨", Modifier.fontSize(1.2.cssRem))
                    Column {
                        SpanText(
                            Txt.concept.stepTitle,
                            Modifier.fontWeight(FontWeight.Bold).fontSize(1.cssRem).color(theme.colors.textPrimary)
                        )
                        SpanText(
                            Txt.concept.stepHint,
                            Modifier.fontSize(0.75.cssRem).color(theme.colors.textSecondary)
                        )
                    }
                }

                Box(TokenSavingBadgeStyle.toModifier()) {
                    SpanText(Txt.concept.savingBadge)
                }
            }

            // Sketches: one large preview, or a 2×2 grid to pick the best of several variants.
            val variants = concept.variantUrls.ifEmpty { listOf(concept.imageUrl) }
            var selected by remember(concept.conceptId, variants) { mutableStateOf(variants.first()) }
            if (variants.size == 1) {
                Box(
                    ConceptPreviewBoxStyle.toModifier(),
                    contentAlignment = Alignment.Center
                ) {
                    Img(
                        src = BackendEndpoint.resolveAssetUrl(variants.first()),
                        alt = Txt.concept.imageAlt,
                        attrs = Modifier
                            .fillMaxHeight()
                            .toAttrs {
                                style {
                                    property("object-fit", "contain")
                                }
                            }
                    )
                }
            } else {
                SpanText(Txt.quality.pickVariant, Modifier.fontSize(0.8.cssRem).color(theme.colors.textSecondary))
                Div(
                    Modifier.fillMaxWidth().gap(0.5.cssRem)
                        .styleModifier {
                            property("display", "grid")
                            property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
                        }
                        .toAttrs()
                ) {
                    variants.forEachIndexed { index, url ->
                        val isSelected = url == selected
                        Box(
                            Modifier
                                .fillMaxWidth()
                                .borderRadius(theme.radii.sm)
                                .border(if (isSelected) 3.px else 1.px, LineStyle.Solid, if (isSelected) theme.colors.accent else theme.colors.border)
                                .backgroundColor(Colors.White)
                                .cursor(Cursor.Pointer)
                                .overflow(Overflow.Hidden)
                                .styleModifier { property("aspect-ratio", "1 / 1") }
                                .onClick { if (!isApproving && !isRerolling) selected = url }
                                .attrsModifier {
                                    attr("role", "radio")
                                    attr("aria-checked", isSelected.toString())
                                    attr("aria-label", Txt.quality.variantLabel(index + 1))
                                    attr("tabindex", "0")
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            Img(
                                src = BackendEndpoint.resolveAssetUrl(url),
                                alt = Txt.quality.variantLabel(index + 1),
                                attrs = Modifier.fillMaxSize().toAttrs { style { property("object-fit", "contain") } }
                            )
                        }
                    }
                }
            }

            // Expanded prompt info
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .backgroundColor(theme.colors.surfaceElevated)
                    .borderRadius(theme.radii.sm)
                    .padding(0.75.cssRem)
                    .border(1.px, LineStyle.Solid, theme.colors.border)
                    .gap(0.375.cssRem)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().cursor(Cursor.Pointer).onClick { showPromptDetails = !showPromptDetails },
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    SpanText(
                        Txt.concept.promptExpansion(showPromptDetails),
                        Modifier.fontSize(0.8.cssRem).fontWeight(FontWeight.SemiBold).color(theme.colors.accent)
                    )
                    SpanText(
                        if (showPromptDetails) "▲" else "▼",
                        Modifier.fontSize(0.75.cssRem).color(theme.colors.textSecondary)
                    )
                }

                if (showPromptDetails) {
                    Div(
                        Modifier
                            .fontSize(0.75.cssRem)
                            .color(theme.colors.textSecondary)
                            .lineHeight(1.4)
                            .toAttrs()
                    ) {
                        SpanText(concept.expandedPrompt)
                    }
                } else {
                    SpanText(
                        concept.expandedPrompt.take(120) + if (concept.expandedPrompt.length > 120) "..." else "",
                        Modifier.fontSize(0.75.cssRem).color(theme.colors.textSecondary)
                    )
                }
            }

            // Quality: the 3D level for "Make 3D", the sketch level for "Draw another".
            MeshQualityChoices(costOf = costOf, enabled = !isApproving && !isRerolling)

            // Action Buttons
            Row(
                modifier = Modifier.fillMaxWidth().gap(0.75.cssRem),
                verticalAlignment = Alignment.CenterVertically
            ) {
                AppButton(
                    text = if (isApproving) Txt.concept.reconstructing
                        else costOf(QualityCredits.meshKind(QualityPrefs.mesh))?.let { Txt.quality.approve(it) } ?: Txt.concept.approve,
                    variant = AppButtonVariant.PRIMARY,
                    size = AppButtonSize.MD,
                    enabled = !isApproving && !isRerolling,
                    loading = isApproving,
                    onClick = { onApprove(concept.copy(imageUrl = selected)) },
                    modifier = Modifier.weight(1f)
                )

                AppButton(
                    text = if (isRerolling) Txt.concept.regenerating else Txt.concept.regenerate,
                    variant = AppButtonVariant.OUTLINE,
                    size = AppButtonSize.MD,
                    enabled = !isApproving && !isRerolling,
                    loading = isRerolling,
                    onClick = { onReroll(concept) }
                )

                AppButton(
                    text = "✕",
                    variant = AppButtonVariant.SECONDARY,
                    size = AppButtonSize.MD,
                    enabled = !isApproving,
                    onClick = onDismiss
                )
            }
        }
    }
}
